import React, { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CreditCard, ArrowRight, ExternalLink, Loader2, ShieldCheck, Award } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import api, { formatApiError } from "@/lib/api";
import { toast } from "@/components/ui/sonner";

// Plan_key → display label mapping (kept small; the backend catalog is source of truth).
const PLAN_LABEL = {
    employer_free: { name: "Free", price: "$0", period: "forever" },
    employer_starter: { name: "Starter", price: "$99", period: "/month" },
    employer_growth: { name: "Growth", price: "$249", period: "/month" },
    employer_enterprise: { name: "Enterprise", price: "Custom", period: "" },
};

// iter-158 — Toast messages triggered when the portal deep-link endpoint
// bounces an email button back here instead of into Stripe (expired
// token / no stripe_customer / Stripe API error). We render them via
// sonner once per load, then strip the query param so a refresh doesn't
// re-fire the toast.
const PORTAL_LINK_TOASTS = {
    expired: {
        kind: "info",
        message: "That billing link has expired — tap Manage billing below for a fresh portal session.",
    },
    no_customer: {
        kind: "info",
        message: "We don't have a Stripe customer on file yet. Start a subscription and the billing portal will unlock.",
    },
    error: {
        kind: "error",
        message: "The Stripe portal couldn't open just now — please try again, or reply to the email for help.",
    },
};

export default function EmployerBilling() {
    const [sub, setSub] = useState(null);
    const [loading, setLoading] = useState(true);
    const [portalBusy, setPortalBusy] = useState(false);
    const [searchParams, setSearchParams] = useSearchParams();

    // iter-158 — Catch ``?portal_link=<expired|no_customer|error>`` and
    // surface a friendly toast + clear the param so a refresh doesn't
    // re-fire. Runs once on mount.
    useEffect(() => {
        const flag = searchParams.get("portal_link");
        if (flag && PORTAL_LINK_TOASTS[flag]) {
            const { kind, message } = PORTAL_LINK_TOASTS[flag];
            if (kind === "error") toast.error(message); else toast.info(message);
            const next = new URLSearchParams(searchParams);
            next.delete("portal_link");
            setSearchParams(next, { replace: true });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const load = useCallback(async () => {
        try {
            setLoading(true);
            const { data } = await api.get("/billing/subscription");
            setSub(data);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
        }
    }, []);
    useEffect(() => { load(); }, [load]);

    const openPortal = async (opts = {}) => {
        try {
            setPortalBusy(true);
            // iter-176 — Spec #2: pass ``flow=cancel`` to open the
            // Stripe portal directly on the subscription-cancel page
            // (no retention wall, no delays).
            const { data } = await api.post("/billing/customer-portal", opts || {});
            window.location.assign(data.url);
        } catch (err) {
            toast.error(formatApiError(err));
            setPortalBusy(false);
        }
    };

    const planId = sub?.plan_id || "employer_free";
    const meta = PLAN_LABEL[planId] || { name: planId, price: "", period: "" };
    const isPaid = planId !== "employer_free" && planId !== "employer_enterprise";

    return (
        <div data-testid="employer-billing-page">
            <div className="mb-6">
                <p className="overline">Account</p>
                <h1 data-testid="dash-page-title" className="mt-1 font-heading font-medium text-2xl sm:text-3xl tracking-tight text-foreground">
                    Billing &amp; Subscription
                </h1>
                <p className="text-sm text-muted-foreground mt-1">
                    Manage your Simonara subscription, view invoice history, and update payment methods.
                </p>
            </div>

            {loading ? (
                <div className="py-16 flex justify-center"><Loader2 size={18} className="animate-spin text-emerald-500" /></div>
            ) : (
                <>
                    {/* Current plan card */}
                    <div className="card-soft p-6 mb-4" data-testid="billing-current-plan">
                        <div className="flex items-start justify-between gap-4 flex-wrap">
                            <div>
                                <div className="overline">Current plan</div>
                                <div className="mt-2 flex items-baseline gap-2">
                                    <span className="font-heading font-bold text-3xl text-foreground">{meta.name}</span>
                                    {meta.price && (
                                        <span className="text-sm text-muted-foreground">
                                            {meta.price}{meta.period}
                                        </span>
                                    )}
                                </div>
                                <div className="mt-2 flex items-center gap-2 text-xs">
                                    <Badge className="border border-border bg-muted/40 text-foreground text-[10px]">
                                        {sub?.status === "active" ? "Active" : sub?.status || "Free"}
                                    </Badge>
                                    {sub?.cancel_at_period_end && (
                                        <Badge className="border border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-400 text-[10px]">
                                            Cancels at period end
                                        </Badge>
                                    )}
                                    {sub?.billing_cycle && (
                                        <span className="text-muted-foreground">Billed {sub.billing_cycle}</span>
                                    )}
                                </div>
                            </div>
                            <div className="flex flex-col sm:flex-row items-stretch gap-2">
                                <Link
                                    to="/pricing"
                                    className="btn-outline text-sm inline-flex items-center justify-center gap-1"
                                    data-testid="billing-view-pricing"
                                >
                                    View plans <ArrowRight size={13} />
                                </Link>
                                {isPaid && (
                                    <>
                                        <button
                                            onClick={() => openPortal()}
                                            disabled={portalBusy}
                                            className="btn-green text-sm inline-flex items-center justify-center gap-1 disabled:opacity-60"
                                            data-testid="billing-open-portal"
                                        >
                                            {portalBusy
                                                ? <Loader2 size={13} className="animate-spin" />
                                                : <><ExternalLink size={13} /> Manage billing</>}
                                        </button>
                                        <button
                                            onClick={() => openPortal({ flow: "cancel" })}
                                            disabled={portalBusy || sub?.cancel_at_period_end}
                                            className="btn-outline text-sm inline-flex items-center justify-center gap-1 border-rose-500/40 text-rose-600 hover:bg-rose-500/10 disabled:opacity-60"
                                            data-testid="billing-cancel-subscription"
                                            title={sub?.cancel_at_period_end ? "Already set to cancel at period end" : "Opens Stripe to cancel in one step"}
                                        >
                                            Cancel subscription
                                        </button>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Upgrade CTA for free plan */}
                    {!isPaid && (
                        <div className="card-soft p-6 border-emerald-500/30 bg-emerald-500/5" data-testid="billing-upgrade-cta">
                            <div className="flex items-start justify-between gap-4 flex-wrap">
                                <div className="max-w-2xl">
                                    <div className="inline-flex items-center gap-1 rounded-full bg-emerald-500 text-white px-2 py-0.5 text-[10px] uppercase tracking-wider font-bold">
                                        <Award size={11} /> Unlock the AI stack
                                    </div>
                                    <h3 className="mt-3 font-heading font-medium text-xl text-foreground">
                                        Upgrade to Growth for unlimited Hiring Briefs™
                                    </h3>
                                    <p className="mt-2 text-sm text-muted-foreground">
                                        $249/month unlocks unlimited jobs, unlimited AI Hiring Briefs, 25 Talent Passports™ per job, and team collaboration for up to 4 seats.
                                    </p>
                                </div>
                                <Link to="/pricing" className="btn-green text-sm inline-flex items-center gap-1" data-testid="billing-upgrade-cta-btn">
                                    See all plans <ArrowRight size={13} />
                                </Link>
                            </div>
                        </div>
                    )}

                    {/* Refund policy reminder */}
                    <div className="card-soft p-4 mt-4 flex items-start gap-2">
                        <ShieldCheck size={14} className="text-emerald-500 mt-0.5 shrink-0" />
                        <p className="text-xs text-muted-foreground">
                            Cancel any time from the Customer Portal — you keep access until the end of your paid billing period.
                            No partial refunds. See the <Link to="/refund-policy" className="underline hover:text-foreground">refund policy</Link> for details.
                        </p>
                    </div>
                </>
            )}
        </div>
    );
}
